import ReportBrowserErrors from '@/graphql/mutations/ReportBrowserErrors'
import cognitoAuth from '@/cognito'

let appVersion = 'unknown'
let lastKnownToken = null
let lastKnownTokenExpiresAt = 0
let lastKnownTokenUserId = null
let currentUserId = () => null
const queue = []
const recentErrors = new Map()
let flushTimerId = null
const FLUSH_INTERVAL_MS = 5000
const MAX_BATCH_SIZE = 20
const DEDUP_WINDOW_MS = 60000
const HELD_REPORTS_KEY = 'errorReporter.heldReports'
const TOKEN_EXPIRY_MARGIN_MS = 10 * 1000

const usableLastKnownToken = () => (Date.now() < lastKnownTokenExpiresAt - TOKEN_EXPIRY_MARGIN_MS ? lastKnownToken : null)

const belongsToUser = (report, userId) => !report.userId || report.userId === userId

const withUser = (reports) => reports.filter((report) => report.userId)

const getErrorSignature = (message, stack) => {
  const firstLine = (stack || '').split('\n')[0] || ''

  return `${message}::${firstLine}`
}

const isDuplicate = (signature) => {
  const now = Date.now()
  const lastSeen = recentErrors.get(signature)

  if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) {
    return true
  }

  recentErrors.set(signature, now)

  if (recentErrors.size > 100) {
    for (const [key, timestamp] of recentErrors) {
      if (now - timestamp > DEDUP_WINDOW_MS) {
        recentErrors.delete(key)
      }
    }
  }

  return false
}

const scheduleFlush = () => {
  if (flushTimerId) {
    return
  }

  flushTimerId = setTimeout(() => {
    flushTimerId = null
    flush()
  }, FLUSH_INTERVAL_MS)
}

const buildRequestBody = (batch) => JSON.stringify({
  operationName: 'reportBrowserErrors',
  query: ReportBrowserErrors,
  variables: { errors: JSON.stringify(batch) }
})

const readHeldReports = () => {
  try {
    return JSON.parse(localStorage.getItem(HELD_REPORTS_KEY)) || []
  } catch (e) {
    return []
  }
}

const writeHeldReports = (reports) => {
  try {
    if (reports.length === 0) {
      localStorage.removeItem(HELD_REPORTS_KEY)
    } else {
      localStorage.setItem(HELD_REPORTS_KEY, JSON.stringify(reports.slice(-MAX_BATCH_SIZE)))
    }
  } catch (e) {}
}

const postReports = (endpoint, authToken, reports) => fetch(endpoint, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Authorization: authToken
  },
  body: buildRequestBody(reports),
  keepalive: true
})

const sendErrors = async (batch) => {
  const endpoint = process.env.VUE_APP_APPSYNC_GRAPHQL_ENDPOINT
  if (!endpoint) {
    return
  }

  let session
  try {
    session = await cognitoAuth.getIdTokenWithExpiryPromise()
  } catch (e) {
    // Auth unavailable — fall back to last known token
  }

  if (session) {
    lastKnownToken = session.token
    lastKnownTokenExpiresAt = session.expiresAt
    lastKnownTokenUserId = session.userId
  }

  const held = readHeldReports()
  const authToken = session?.token || usableLastKnownToken()
  if (!authToken) {
    writeHeldReports(withUser([...held, ...batch]))
    return
  }

  const ownHeld = held.filter((report) => belongsToUser(report, lastKnownTokenUserId))
  const ownBatch = batch.filter((report) => belongsToUser(report, lastKnownTokenUserId))
  const responses = await Promise.all([ownHeld, ownBatch]
    .filter((reports) => reports.length > 0)
    .map((reports) => postReports(endpoint, authToken, reports)))

  writeHeldReports(responses.some((response) => response.status === 401) ? withUser([...ownHeld, ...ownBatch]) : [])
}

const flush = () => {
  if (queue.length === 0 && readHeldReports().length === 0) {
    return
  }

  const batch = queue.splice(0, MAX_BATCH_SIZE)

  try {
    sendErrors(batch).catch(() => {})
  } catch (e) {
    // Silently drop
  }

  if (queue.length > 0) {
    scheduleFlush()
  }
}

const init = ({ version, currentUserId: getCurrentUserId }) => {
  appVersion = version || 'unknown'
  if (getCurrentUserId) {
    currentUserId = getCurrentUserId
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (queue.length === 0) {
        return
      }

      const endpoint = process.env.VUE_APP_APPSYNC_GRAPHQL_ENDPOINT
      if (!endpoint) {
        return
      }

      const batch = queue.splice(0, MAX_BATCH_SIZE)
      const authToken = usableLastKnownToken()
      if (!authToken) {
        writeHeldReports([...readHeldReports(), ...withUser(batch)])
        return
      }

      const ownBatch = batch.filter((report) => belongsToUser(report, lastKnownTokenUserId))
      if (ownBatch.length === 0) {
        return
      }

      try {
        fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken
          },
          body: buildRequestBody(ownBatch),
          keepalive: true
        }).catch(() => {})
      } catch (e) {
        // Silently drop
      }
    })
  }
}

const reportError = ({ message, stack, type, extra }) => {
  try {
    const signature = getErrorSignature(message, stack)
    if (isDuplicate(signature)) {
      return
    }

    let safeExtra = {}
    try {
      safeExtra = JSON.parse(JSON.stringify(extra || {}))
    } catch (e) {
      safeExtra = { serializationError: 'extra contained non-serializable data' }
    }

    queue.push({
      message: message || 'Unknown error',
      stack: stack || '',
      type: type || 'unknown',
      url: typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      timestamp: new Date().toISOString(),
      appVersion,
      userId: currentUserId(),
      extra: safeExtra
    })

    scheduleFlush()
  } catch (e) {
    // Never throw from the error reporter
  }
}

export default { init, reportError, flush }
